import { useState } from "react";
import { IonLabel } from "@ionic/react";
import { getSchoolEarliestStartDateTime } from "components/RequestForm/hooks";
import { DateTime } from "luxon";
import { SchoolClosure, SchoolHoursModel } from "model";
import { useAuth } from "pages";
import { Asterisk, CheckboxInput, DateInput, type DisabledDateRanges } from "swing-components";

import { SubScheduleDay } from "~components/RequestForm/utils";
import { translate } from "~utils";

export type MultiDateField = {
  /** The current datetime in the Schools timezone */
  now: DateTime;
  schoolHours: SchoolHoursModel;
  // FIXME: This is optional due to feature flagging.
  // TODO: Remove optional in https://linear.app/swing-education/issue/MKTP-946
  schoolClosures?: Array<SchoolClosure>;
  start: DateTime;
  end: DateTime;
  /** Need the cached end date of the request, so we can limit it when extending a filled request */
  originalEndDate?: DateTime;
  onChange: (params: { startDate?: DateTime; endDate?: DateTime }) => void;
  isWaitlistedOrFilledReq?: boolean;
  /** The sub schedule for the request */
  subSchedule?: Array<SubScheduleDay>;
};

export function MultiDateField(props: MultiDateField) {
  /*** Props ****/
  const {
    now,
    schoolHours,
    schoolClosures,
    start,
    end,
    originalEndDate,
    onChange,
    isWaitlistedOrFilledReq,
    subSchedule,
  } = props;

  /*** Constants ***/
  // This is the earliest date and time which a request can be start.
  const earliestStartDateTime = getSchoolEarliestStartDateTime({ now, schoolHours });
  const [allowNSD, setAllowNSD] = useState(false);
  const { userInfo } = useAuth();
  const showNSDToggle = userInfo.isSuperAdmin;

  /*** Disabled Dates ***/
  const schoolClosuresDisabledDates: DisabledDateRanges =
    schoolClosures?.map((closure) => ({
      start: DateTime.fromISO(closure.start).toISO(),
      // NOTE: Since the end date of a School Closure is the end of the School
      // Hours, then that the end date is also included. Due to this, we must
      // add 1 day to the end date since `end` is exclusive.
      end: DateTime.fromISO(closure.end).plus({ day: 1 }).toISO(),
      label: translate("MULTI_DATE_FIELD_DISABLED_SCHOOL_CLOSURE_LABEL"),
    })) ?? [];

  // Disable the following dates in the calendar
  const startDisabledDates: DisabledDateRanges = [
    {
      end: earliestStartDateTime.toISO(),
      label: translate("MULTI_DATE_FIELD_DISABLED_START_LABEL"),
    },
    ...(!allowNSD ? schoolClosuresDisabledDates : []),
  ];

  // Disable the days that are in conflict with the sub schedule
  const conflictingDays: DisabledDateRanges =
    subSchedule?.map((day) => ({
      start: day.start.toISO(),
      end: day.start.plus({ day: 1 }).toISO(),
      label: translate("LABEL_SUB_UNAVAILABLE_DAYS"),
    })) ?? [];

  const endDisabledDates: DisabledDateRanges = [
    {
      end: originalEndDate ? originalEndDate.toISO() : start.toISO(),
      label: translate("MULTI_DATE_FIELD_DISABLED_END_LABEL", { startDate: start }),
    },
    ...conflictingDays,
    ...(!allowNSD ? schoolClosuresDisabledDates : []),
  ];

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <IonLabel className="font-medium">
        {translate("LABEL_DATE")}
        <Asterisk />
      </IonLabel>

      {showNSDToggle && (
        <CheckboxInput
          label="Allow non-school days to be selected"
          onChange={setAllowNSD}
          isChecked={allowNSD}
        />
      )}

      <div css={{ display: "flex", gap: 8, alignItems: "center" }}>
        <DateInput
          // Since this component is only showing handling days and not times,
          // we are purposefully ignore hours, minutes, seconds, milliseconds so
          // that upstreams functions process the date only, with no time bias.
          value={start.startOf("day").toISO()}
          // The assumption is that the parent will have set the Luxon default
          // timezone to be the School's timezone, thus making this ISO to
          // DateTime transformation safe.
          onChange={(start) => onChange({ startDate: DateTime.fromISO(start) })}
          disabledDateRanges={startDisabledDates}
          isDisabled={isWaitlistedOrFilledReq}
          isFluid
        />
        -
        <DateInput
          // Since this component is only showing handling days and not times,
          // we are purposefully ignore hours, minutes, seconds, milliseconds so
          // that upstreams functions process the date only, with no time bias.
          value={end.startOf("day").toISO()}
          // The assumption is that the parent will have set the Luxon default
          // timezone to be the School's timezone, thus making this ISO to
          // DateTime transformation safe.
          onChange={(end) => onChange({ endDate: DateTime.fromISO(end) })}
          disabledDateRanges={endDisabledDates}
          isFluid
        />
      </div>
    </div>
  );
}
